import React, { useState, useRef, useEffect } from 'react';
import { CategorizedField } from '../../utils/fieldGrouping';
import { useAuth } from '../../contexts/Auth/authContext';
import './FieldGroupSection.css';
interface FieldGroupSectionProps {
  title: string;
  code: string;
  isExpanded: boolean;
  onToggle: () => void;
  fieldCount?: number;
  children: React.ReactNode;
}

export const FieldGroupSection: React.FC<FieldGroupSectionProps> = ({
  title,
  code,
  isExpanded,
  onToggle,
  fieldCount,
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | 'auto'>(0);
  const [useAutoHeight, setUseAutoHeight] = useState(false);

  // Calculate content height for smooth animation, then switch to auto
  useEffect(() => {
    if (contentRef.current) {
      if (isExpanded) {
        // First set to scrollHeight for animation
        setContentHeight(contentRef.current.scrollHeight);
        setUseAutoHeight(false);
        // After animation completes, switch to auto to allow dynamic content
        const timer = setTimeout(() => {
          setUseAutoHeight(true);
        }, 300); // Match CSS transition duration
        return () => clearTimeout(timer);
      } else {
        setUseAutoHeight(false);
        setContentHeight(0);
      }
    }
  }, [isExpanded]);

  // Recalculate when content changes while expanded
  useEffect(() => {
    if (isExpanded && contentRef.current && !useAutoHeight) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children, isExpanded, useAutoHeight]);

  return (
    <div className={`field-group-section ${isExpanded ? 'expanded' : 'collapsed'}`} data-group={code}>
      <button
        className="field-group-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={`field-group-content-${code}`}
      >
        <div className="field-group-header-left">
          <span className={`chevron ${isExpanded ? 'rotated' : ''}`}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <span className="field-group-title">{title}</span>
        </div>
        {fieldCount !== undefined && (
          <span className="field-group-count">{fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
        )}
      </button>

      <div
        id={`field-group-content-${code}`}
        className="field-group-content"
        ref={contentRef}
        style={{ height: isExpanded && useAutoHeight ? 'auto' : contentHeight }}
      >
        <div className="field-group-content-inner">
          {children}
        </div>
      </div>
    </div>
  );
};


interface FieldInputProps {
  field: CategorizedField;
  isEditing: boolean;
  onChange: (fieldName: string, value: any) => void;
  value?: any;  // Override value from form state
}

/**
 * Individual field input component
 * Renders appropriate input type based on field configuration
 */
export const FieldInput: React.FC<FieldInputProps> = ({
  field,
  isEditing,
  onChange,
  value: overrideValue,
}) => {
  const displayValue = overrideValue !== undefined ? overrideValue : field.value;
  const [showPassword, setShowPassword] = useState(false);
  const { user } = useAuth();
  const canRevealPassword = user?.roles?.includes('SuperAdmin') || user?.roles?.includes('Admin') || false;

  const renderValue = (): React.ReactNode => {
    if (field.type === 'password') {
      if (!displayValue) return '-';
      if (canRevealPassword && showPassword) return String(displayValue);
      return '••••••••';
    }
    if (field.type === 'boolean') {
      return displayValue ? 'Yes' : 'No';
    }
    if (field.type === 'array') {
      if (Array.isArray(displayValue) && displayValue.length > 0) {
        return displayValue.join(', ');
      }
      return '-';
    }
    if (field.type === 'json') {
      if (displayValue && typeof displayValue === 'object') {
        const jsonStr = JSON.stringify(displayValue, null, 2);
        return <pre className="field-value-json">{jsonStr}</pre>;
      }
      if (typeof displayValue === 'string' && displayValue.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(displayValue);
          return <pre className="field-value-json">{JSON.stringify(parsed, null, 2)}</pre>;
        } catch {
          return displayValue;
        }
      }
      return '-';
    }
    return displayValue !== null && displayValue !== undefined ? String(displayValue) : '-';
  };

  const renderInput = () => {
    if (!field.editable) {
      return <span className="field-value readonly">{renderValue()}</span>;
    }

    const tid = `field-device-setting-${field.name}`;
    switch (field.type) {
      case 'number':
        return (
          <input
            type="number"
            className="field-input"
            value={displayValue || 0}
            onChange={(e) => onChange(field.name, parseInt(e.target.value) || 0)}
            data-testid={tid}
          />
        );

      case 'password':
        return (
          <input
            type="password"
            className="field-input"
            value={displayValue || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder="••••••••"
            data-testid={tid}
          />
        );

      case 'boolean':
        return (
          <select
            className="field-input field-select"
            value={displayValue ? 'true' : 'false'}
            onChange={(e) => onChange(field.name, e.target.value === 'true')}
            data-testid={tid}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      case 'json':
        return (
          <textarea
            className="field-input field-input-json"
            value={typeof displayValue === 'object' ? JSON.stringify(displayValue, null, 2) : (displayValue || '')}
            onChange={(e) => {
              try {
                onChange(field.name, JSON.parse(e.target.value));
              } catch {
                onChange(field.name, e.target.value);
              }
            }}
            rows={4}
            data-testid={tid}
          />
        );

      default:
        // conf47Type ("voice / local_alarm / dtmf_server") is intentionally
        // no longer surfaced in the admin UI — the value is kept on the
        // wire for firmware retro-compat and defaulted server-side. New
        // hardware-specific configuration should be modeled via
        // HardwareFieldDefinition rows on a hardware type instead.
        return (
          <input
            type="text"
            className="field-input"
            value={displayValue || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            data-testid={tid}
          />
        );
    }
  };

  return (
    <div className={`field-item ${!field.editable ? 'readonly' : ''}`}>
      <label className="field-label">{field.displayName}</label>
      {isEditing && field.editable ? renderInput() : (
        field.type === 'password' && canRevealPassword && displayValue ? (
          <span className="field-value field-password-value">
            <span>{renderValue()}</span>
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </span>
        ) : (
          <span className="field-value">{renderValue()}</span>
        )
      )}
    </div>
  );
};

/**
 * Field grid component for rendering multiple fields
 */
interface FieldGridProps {
  fields: CategorizedField[];
  isEditing: boolean;
  formValues: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
}

export const FieldGrid: React.FC<FieldGridProps> = ({
  fields,
  isEditing,
  formValues,
  onChange,
}) => {
  return (
    <div className="field-grid">
      {fields.map((field) => (
        <FieldInput
          key={field.name}
          field={field}
          isEditing={isEditing}
          onChange={onChange}
          value={formValues[field.name]}
        />
      ))}
    </div>
  );
};

export default FieldGroupSection;
